// src/components/StoryJourney.tsx
//
// The emotional spine of the landing: a vertical, scroll-driven story of one
// job-seeker's arc — from the overwhelm of searching, through a tracked
// pipeline and interview prep, to the joy of the offer. Each full-viewport
// scene pairs a real photograph (parallax + dark-neon wash) with Sumbitrbuddy's
// ACTUAL product UI animating in, so every scene shows a real feeling AND the
// app solving that exact problem. Reveals use the page's global `.reveal`
// observer (see Landing). Reduced-motion degrades gracefully (CSS).
import React from "react";
import "../styles/StoryJourney.css";

const img = (id: string) =>
  `https://images.unsplash.com/photo-${id}?w=1600&q=80&auto=format&fit=crop`;

// --- Real product-UI overlays (neon glass) -------------------------------

const SearchUI: React.FC = () => (
  <div className="su-card sj-float">
    <div className="su-head">
      <span className="su-logo">N</span>
      <span className="su-titles">
        <span className="su-role">Product Designer</span>
        <span className="su-co">Northwind · Toronto, ON</span>
      </span>
      <span className="su-score">
        <b>94</b>
        <small>%</small>
      </span>
    </div>
    <div className="su-bar">
      <i style={{ width: "94%" }} />
    </div>
    <div className="su-tags">
      <span>Experience match</span>
      <span>Education match</span>
    </div>
    <div className="su-foot">
      <span>🔎 5 boards, one search</span>
      <span>📍 Your region only</span>
    </div>
  </div>
);

const EmailTrackUI: React.FC = () => (
  <div className="et-card sj-float">
    <div className="et-head">
      <span className="et-live" /> Inbox · auto-tracked
    </div>
    <div className="et-mail">
      <span className="et-avatar">N</span>
      <span className="et-mail-body">
        <span className="et-mail-from">Northwind Talent</span>
        <span className="et-mail-sub">Interview invitation — Product Designer</span>
      </span>
    </div>
    <div className="et-tag">
      <span className="tu-toast-ic">✓</span>
      Auto-logged &amp; moved to <b>Interview</b>
    </div>
  </div>
);

const PrepUI: React.FC = () => (
  <div className="pu-card sj-float">
    <div className="pu-head">
      <span className="pu-live" /> Interview prep · unlocked
    </div>
    <ul className="pu-list">
      <li>Tell me about a design system you scaled.</li>
      <li>How do you handle conflicting stakeholder feedback?</li>
      <li>Walk me through a project you're proud of.</li>
    </ul>
    <div className="pu-foot">Start mock interview →</div>
  </div>
);

const OfferUI: React.FC = () => (
  <div className="ou-card sj-float">
    <span className="ou-confetti" aria-hidden="true">
      {Array.from({ length: 14 }).map((_, i) => (
        <i key={i} style={{ ["--i" as string]: String(i) }} />
      ))}
    </span>
    <div className="ou-badge">OFFER RECEIVED</div>
    <div className="ou-grid">
      <div>
        <small>Base</small>
        <b>$98k</b>
      </div>
      <div>
        <small>Bonus</small>
        <b>$12k</b>
      </div>
      <div className="ou-total">
        <small>Total comp</small>
        <b>$124k</b>
      </div>
    </div>
    <div className="ou-foot">Compare offers &amp; negotiate with clarity</div>
  </div>
);

// --- Scene data -----------------------------------------------------------

type SceneDef = {
  id: string;
  image: string;
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  ui: React.FC;
};

const SCENES: SceneDef[] = [
  {
    id: "search",
    image: img("1486312338219-ce68d2c6f44d"),
    eyebrow: "01 · The search",
    title: (
      <>
        The overwhelm,{" "}
        <span className="sj-accent sj-accent-cyan">gone.</span>
      </>
    ),
    body:
      "No more twelve tabs and endless scrolling. Sumbitrbuddy searches every major board across your country and province at once — then scores each role against your resume, so the best fits rise to the top.",
    ui: SearchUI,
  },
  {
    id: "track",
    image: img("1515378791036-0648a3ef77b2"),
    eyebrow: "02 · The follow-up",
    title: (
      <>
        Never lose{" "}
        <span className="sj-accent sj-accent-indigo">the thread.</span>
      </>
    ),
    body:
      "Connect your inbox and replies file themselves — interview invites, rejections, and offers detected and logged against the right application, automatically. You never chase a status again.",
    ui: EmailTrackUI,
  },
  {
    id: "prep",
    image: img("1758518730162-09a142505bfd"),
    eyebrow: "03 · The interview",
    title: (
      <>
        Walk in{" "}
        <span className="sj-accent sj-accent-violet">ready.</span>
      </>
    ),
    body:
      "The moment an application reaches the interview stage, Sumbitrbuddy unlocks role-specific questions and mock interviews — so you show up prepared, not panicked.",
    ui: PrepUI,
  },
  {
    id: "offer",
    image: img("1503266980949-bd30d04d0b7a"),
    eyebrow: "04 · The offer",
    title: (
      <>
        The moment it all{" "}
        <span className="sj-accent sj-accent-green">pays off.</span>
      </>
    ),
    body:
      "Compare offers side by side — base, bonus, total comp — and negotiate from a position of clarity. This is what the whole journey was for.",
    ui: OfferUI,
  },
];

const StoryJourney: React.FC = () => {
  return (
    <div className="sj" id="story">
      <div className="sj-intro">
        <div className="lp-wrap sj-intro-inner reveal">
          <span className="sj-intro-eyebrow">One journey</span>
          <h2 className="sj-intro-title">From lost to landed — scroll the whole arc.</h2>
          <p className="sj-intro-sub">
            Follow it the way you'll live it: the search, the pipeline, the
            interview, the offer — and how Sumbitrbuddy carries you through each.
          </p>
        </div>
      </div>

      {SCENES.map((s, i) => {
        const UI = s.ui;
        return (
          <section
            className={`sj-scene sj-scene-${i % 2 === 0 ? "l" : "r"}`}
            key={s.id}
            aria-labelledby={`sj-h-${s.id}`}
          >
            <div
              className="sj-photo"
              style={{ backgroundImage: `url(${s.image})` }}
              aria-hidden="true"
            />
            <div className="sj-wash" aria-hidden="true" />
            <div className="lp-wrap sj-inner">
              <div className="sj-copy reveal">
                <span className="sj-eyebrow">{s.eyebrow}</span>
                <h2 className="sj-title" id={`sj-h-${s.id}`}>
                  {s.title}
                </h2>
                <p className="sj-body">{s.body}</p>
              </div>
              <div className="sj-ui reveal">
                <UI />
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default StoryJourney;
